import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { NoticesApi } from '@apply/api';
import { LoadPanel } from 'devextreme-react/load-panel';
import { handleAdminError, handleAdminSuccess } from '../../../../../../../apps/admin/src/common/SharedFunctions';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { IPostClosingCertNoticeQueueItem, IPostClosingCertRecipientList } from '@apply/api/src/api/NoticesApi';

const styles = {
    buttonMarginRight: { marginRight: '10px' },
    buttonContainer: {
        textAlign: 'right',
        borderTop: '2px solid silver',
        padding: '10px 10px',
    },
    form: { paddingLeft: '5px', paddingTop: '10px' },
    formRow: { marginBottom: '0' },
};

interface IPostClosingCertificationModalProps {
    applicationInsights: ApplicationInsights;
    loanId: number | undefined;
    loanPropertyId: number | undefined;
    isOpen: boolean;
    onClose(): void;
}

export const PostClosingCertificationModal: React.FC<IPostClosingCertificationModalProps> = ({
    applicationInsights,
    loanId,
    loanPropertyId,
    isOpen,
    onClose,
}) => {
    const [canSubmit, set_canSubmit] = useState(false);
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage] = useState('Loading...');
    const [postClosingCertRecipientId, set_postClosingCertRecipientId] = useState<number>();
    const [postClosingRecipientOptions, set_postClosingRecipientOptions] = useState<IPostClosingCertRecipientList[]>([]);

    const noticesApi = new NoticesApi.NoticesApi(applicationInsights);

    const loadPostClosingCertRecipients = async () => {
        if (loanId != undefined) {
            const data = await noticesApi.getPostClosingCertRecipientListByLoan(loanId);
            set_postClosingRecipientOptions(data.result);
        }
    };

    useEffect(() => {
        const loadListsAsync = async () => {
            await loadPostClosingCertRecipients();
        };
        set_loadingOpen(true);
        loadListsAsync();
        set_loadingOpen(false);
        set_postClosingCertRecipientId(undefined);
    }, [isOpen, loanId]);

    useEffect(() => {
        set_canSubmit(
            postClosingCertRecipientId !== null && postClosingCertRecipientId !== undefined && postClosingCertRecipientId > 0
        );
    }, [postClosingCertRecipientId]);

    const _handleClose = () => {
        onClose();
    };

    const sendPostClosingCertification = async (e) => {
        e.preventDefault();

        // Guard clause
        if (!loanId || !loanPropertyId || !postClosingCertRecipientId) {
            handleAdminError(new Error('Validation Error'), 'Missing required fields.');
            return;
        }

        try {
            set_loadingOpen(true);

            const model: IPostClosingCertNoticeQueueItem = {
                principalId: postClosingCertRecipientId,
                loanId: loanId,
                propertyId: loanPropertyId,
            };

            const response = await noticesApi.queuePostClosingCertNotice(model);

            if (response?.statusCode == 200) {
                handleAdminSuccess('Post Closing Certification notice queued successfully.');
                onClose();
            } else {
                handleAdminError(new Error('Generation Error'), 'Unable to queue certification notice.');
            }
        } catch (err) {
            handleAdminError(err);
        } finally {
            set_loadingOpen(false);
        }
    };

    return (
        <>
            <Dialog
                maxWidth='md'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '550px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='postClosingCertificationForm'
                                    onSubmit={(event) => sendPostClosingCertification(event)}
                                >
                                    <div>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1 style={{ whiteSpace: 'nowrap' }}>Post Closing</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div>
                                            <div
                                                style={styles.form}
                                                className={'standard-form'}
                                            >
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        {(postClosingRecipientOptions == undefined ||
                                                            postClosingRecipientOptions.length == 0) && (
                                                            <div>No owners of 20% or more found for this loan.</div>
                                                        )}
                                                        {postClosingRecipientOptions.length > 0 && (
                                                            <div>
                                                                <label htmlFor='postClosingCertRecipient'>
                                                                    Please select the 20% or more Owner you would like this notice
                                                                    sent to:
                                                                </label>
                                                                <select
                                                                    id='postClosingCertRecipient'
                                                                    value={postClosingCertRecipientId}
                                                                    onChange={(event) =>
                                                                        set_postClosingCertRecipientId(Number(event.target.value))
                                                                    }
                                                                >
                                                                    <option value=''></option>
                                                                    {postClosingRecipientOptions.map((t) => {
                                                                        return (
                                                                            <option
                                                                                key={t.principalId}
                                                                                value={t.principalId}
                                                                            >
                                                                                {t.name} ({t.entityName})
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </select>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ padding: '5px 5px' }}>
                                            <div
                                                className='button-footer'
                                                style={styles.buttonContainer}
                                            >
                                                <button
                                                    style={styles.buttonMarginRight}
                                                    type='submit'
                                                    className='button primary'
                                                    disabled={!canSubmit}
                                                >
                                                    Submit
                                                </button>
                                                <button
                                                    type='button'
                                                    className='button hollow'
                                                    data-dismiss='modal'
                                                    onClick={_handleClose}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};
